/**
 * ngPrometZoneFormat — the ONE place the zone view rounds and words a figure
 * (#711). The server sends unrounded numbers (#709); the panel, the report
 * page and the map labels all print through these functions, so a value can
 * never read »14 %« in one place and »13 %« in another.
 */

import type { PrometAreaKind } from '../../../../../../shared/types/ngPromet.js';

export const ZONE_UNIT = 'voz.';
export const NO_VALUE = '—';
export const NO_DATA_LABEL = 'brez podatkov';

const MINUS = '−';

function isNumber(n: number | null | undefined): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

function plain(n: number, maxDecimals: number): string {
  return Math.abs(n).toLocaleString('sl-SI', { maximumFractionDigits: maxDecimals });
}

function roundTo(n: number, decimals: number): number {
  const f = 10 ** decimals;
  // + 0 turns a rounded −0 into 0, so a zero never carries a sign.
  return Math.round(n * f) / f + 0;
}

/** A count as a whole number: 954.07 → »954«. */
export function fmtCount(n: number | null | undefined): string {
  if (!isNumber(n)) return NO_VALUE;
  const r = roundTo(n, 0);
  return (r < 0 ? MINUS : '') + plain(r, 0);
}

/** A difference of counts with its sign: »+220«, »−50«, »0«. */
export function fmtSignedCount(n: number | null | undefined): string {
  if (!isNumber(n)) return NO_VALUE;
  const r = roundTo(n, 0);
  return (r > 0 ? '+' : r < 0 ? MINUS : '') + plain(r, 0);
}

/** A share of an area as whole per cent (the Priloga's form): 13.5 → »14 %«. */
export function fmtSharePct(n: number | null | undefined): string {
  if (!isNumber(n)) return NO_VALUE;
  return `${plain(roundTo(n, 0), 0)} %`;
}

/** A relative difference with its sign, at most one decimal: »+11 %«, »−3,9 %«. */
export function fmtSignedPct(n: number | null | undefined): string {
  if (!isNumber(n)) return NO_VALUE;
  const r = roundTo(n, 1);
  return `${r > 0 ? '+' : r < 0 ? MINUS : ''}${plain(r, 1)} %`;
}

/**
 * The two direction letters of a map label. A zone has »V« (vstopi) and »I«
 * (izstopi), as Priloga 1 prints them; the line has its own two directions.
 */
export function directionLetters(kind: PrometAreaKind): { in: string; out: string } {
  return kind === 'line' ? { in: 'S→J', out: 'J→S' } : { in: 'V', out: 'I' };
}

/** »V: P 14 % | R 10 %« — one direction of one access point, P beside R. */
export function pointShareLabel(
  letter: string,
  pShare: number | null | undefined,
  rShare: number | null | undefined,
): string {
  return `${letter}: P ${fmtSharePct(pShare)} | R ${fmtSharePct(rShare)}`;
}

/**
 * »P: +220 voz.« — the area's vstopi − izstopi for one period. `unit` is the
 * unit of the answer's categories (`prometUnit`, #740): vehicles for a motor
 * choice, '' when the count holds two-wheelers or pedestrians — then »P: +220«.
 */
export function areaNetLabel(period: 'P' | 'R', net: number | null | undefined, unit: string = ZONE_UNIT): string {
  if (!isNumber(net)) return `${period}: ${NO_VALUE}`;
  return `${period}: ${fmtSignedCount(net)}${unit ? ` ${unit}` : ''}`;
}

/** An hour of the day as the range it covers: 10 → »10:00–11:00«. */
export function hourRangeLabel(hour: number): string {
  const two = (h: number) => String(h).padStart(2, '0');
  return `${two(hour)}:00–${two(hour + 1)}:00`;
}
